"use client";

import { AnimatePresence, motion } from "framer-motion";
import { ReactNode, useCallback, useEffect, useRef } from "react";
import { IoClose } from "react-icons/io5";

import styles from "./Filters.module.scss";
import { montserrat } from "./font";

interface Props {
  open: boolean;
  onClose: () => void;
  titleId: string;
  children: ReactNode;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Casca do painel: backdrop, trava de scroll, Esc, foco preso dentro e foco
 * devolvido ao gatilho no fechamento.
 *
 * O `react-outside-click-handler` que existia antes não cobria nada disso.
 */
export default function FiltersDrawer({ open, onClose, titleId, children }: Props) {
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }

      if (event.key !== "Tab" || !panelRef.current) return;

      const focusables = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)
      ).filter((element) => element.offsetParent !== null);

      if (!focusables.length) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (!open) return;

    previouslyFocused.current = document.activeElement as HTMLElement;
    document.addEventListener("keydown", handleKeyDown);

    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";

    panelRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = overflow;
    };
  }, [open, handleKeyDown]);

  return (
    <AnimatePresence
      onExitComplete={() => {
        // Só devolve o foco depois da animação, senão a página salta.
        previouslyFocused.current?.focus();
      }}
    >
      {open && (
        <>
          <motion.div
            key="filters-backdrop"
            className={styles.backdrop}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />

          <motion.div
            key="filters-panel"
            ref={panelRef}
            className={`${styles.panel} ${montserrat.className}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            <header className={styles.panelHeader}>
              <h3 className={styles.title} id={titleId}>
                Filtrar
              </h3>

              <button
                type="button"
                className={styles.close}
                onClick={onClose}
                aria-label="Fechar filtros"
              >
                <IoClose color="var(--primary)" size={20} />
              </button>
            </header>

            {children}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
